import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';

import MessageBox from 'Components/Utility/MessageBox';
import {
  getAccountPreferenceCentre,
  getAccountLists,
  saveLists,
  resetAccountPreferenceCentre,
} from 'Redux/ducks/account-preference-centre';

import 'Components/Account/AccountNotice.scss';
import 'Components/Account/ContactPreferences/PreferenceCenter.scss';

const PreferenceCenter = ({ navigationClick }) => {
  const firstLoad = useRef(true);
  const dispatch = useDispatch();

  const {
    preferenceEmailTypes,
    preferenceProductTypes,
    accountListEmailTypes,
    accountListProductTypes,
    listSaveError,
  } = useSelector(((state) => {
    const accountPreference = state?.accountPreference;
    return {
      preferenceEmailTypes: accountPreference?.preferences.TYPE || [],
      preferenceProductTypes: accountPreference?.preferences.PROD || [],
      accountListProductTypes: accountPreference?.accountLists.PROD || [],
      accountListEmailTypes: accountPreference?.accountLists.TYPE || [],
      listSaveError: accountPreference?.listSaveError,
    };
  }));
  const { formatMessage } = useIntl();
  const [selectedItems, setSelectedItems] = useState({});
  const [editingItems, setEditingItems] = useState(false);

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      dispatch(resetAccountPreferenceCentre());
      dispatch(getAccountPreferenceCentre());
      dispatch(getAccountLists());
    };
    fetchData();
  }, [dispatch]);

  const prevAccountListProductTypes = useRef([]);
  const prevAccountListEmailTypes = useRef([]);

  useEffect(() => {
    const listsHaveChanged =
      !isEqual(prevAccountListProductTypes.current, accountListProductTypes) ||
      !isEqual(prevAccountListEmailTypes.current, accountListEmailTypes);

    if (firstLoad.current || listsHaveChanged) {
      const allChosen = {};
      accountListProductTypes.forEach((productType) => {
        allChosen[productType.code] = true;
      });
      accountListEmailTypes.forEach((emailType) => {
        allChosen[emailType.code] = true;
      });
      setSelectedItems(allChosen);

      // Update the previous lists for the next comparison
      prevAccountListProductTypes.current = accountListProductTypes;
      prevAccountListEmailTypes.current = accountListEmailTypes;
    }
  }, [accountListProductTypes, accountListEmailTypes]);


  const clickPreference = (type) => {
    setEditingItems(true);
    const updatedSelectedItems = { ...selectedItems };

    updatedSelectedItems[type.code] = !updatedSelectedItems[type.code];

    setSelectedItems(updatedSelectedItems);
  };

  const savePreferences = () => {
    const sendArray = [];
    preferenceProductTypes.forEach((productType) => {
      const prodListObject = {
        listName: `PC-PROD-${productType.rawCode}`,
        selected: selectedItems[productType.code] === true,
      };
      sendArray.push(prodListObject);
    });
    preferenceEmailTypes.forEach((emailType) => {
      const emailListObject = {
        listName: `PC-TYPE-${emailType.rawCode}`,
        selected: selectedItems[emailType.code] === true,
      };
      sendArray.push(emailListObject);
    });
    dispatch(saveLists(sendArray, {})); // legacy from PreferenceCenterContainer
    navigationClick('preferencecenter');
    setEditingItems(false);
  };

  const tickArray = Object.keys(selectedItems);

  const showAll = !firstLoad.current || tickArray.length !== 0;

  const messages = defineMessages({
    saveListsSuccess: {
      id: 'confirm-account.save-lists-success',
      defaultMessage: 'We have saved your selections',
    },
    saveListsError: {
      id: 'confirm-account.save-lists-error',
      defaultMessage: 'Error saving selections',
    },
  });

  return (
    <div className="account-content">
      <div>
        <h2>
          <FormattedMessage id="account-preference-center.heading" defaultMessage="Email Content Preferences" />
        </h2>
        <p>
          <FormattedMessage id="account-preference-center.description" defaultMessage="These preferences help us personalize which game deals, bundles and offers you see most often in your emails. You will still receive essential account updates and purchase notifications." />
        </p>
      </div>
      {listSaveError !== null && !listSaveError &&
        <div className="mt-3 mb-3">
          <MessageBox
            type="success"
            title="Save List Success"
            message={formatMessage(messages.saveListsSuccess)}
          />
        </div>
      }
      {listSaveError !== null && listSaveError &&
        <div className="mt-3">
          <MessageBox
            type="error"
            title="Save List Error"
            message={formatMessage(messages.saveListsError)}
          />
        </div>
      }
      {editingItems &&
        <div className="account-notice">
          <div className="message-container">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
            <FormattedMessage id="account-preference-center.notice.message" defaultMessage="Please click 'Update preferences' to save your selections." />
          </div>
          <Button
            color="primary"
            className="btn-account"
            onClick={savePreferences}
          >
            <FormattedMessage id="account-preference-center.update-preferences" defaultMessage="Update Preferences" />
          </Button>
        </div>
      }
      {showAll &&
        <div className="account-table">
          <div className="account-table-heading">
            <FormattedMessage id="account-preference-center.product" defaultMessage="Product Categories" />
          </div>
          <div className="account-table-row account-preference-center">
            <div className="setting-line-item-container">
              {preferenceProductTypes.map(productType =>
                <div className="setting-line-item" key={productType.listName}>
                  <div className="setting-line-item-title">
                    {productType.listName}
                  </div>
                  <div className="setting-line-item-details">
                    <div className="setting-line-item-data">
                      <div className="setting-line-item-data-smallprint">
                        {productType.help}
                      </div>
                    </div>
                    <div className="setting-line-item-link">
                      <div className="switch-container">
                        <label className="switch" htmlFor={productType.code}>
                          <input
                            id={productType.code}
                            name={productType.code}
                            type="checkbox"
                            checked={selectedItems[productType.code] || false}
                            className="ml-1"
                            onChange={() => clickPreference(productType)}
                          />
                          <span className="theme-slider round" />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
      }

      {showAll &&
        <div className="account-table">
          <div className="account-table-heading">
            <FormattedMessage id="account-preference-center.email" defaultMessage="Additional Content" />
          </div>
          <div className="account-table-row account-preference-center">
            <div className="setting-line-item-container">
              {preferenceEmailTypes.map(emailType =>
                <div className="setting-line-item" key={emailType.listName}>
                  <div className="setting-line-item-title">
                    {emailType.listName}
                  </div>
                  <div className="setting-line-item-details">
                    <div className="setting-line-item-data">
                      <div className="setting-line-item-data-smallprint">
                        {emailType.help}
                      </div>
                    </div>
                    <div className="setting-line-item-link">
                      <div className="switch-container">
                        <label className="switch" htmlFor={emailType.code}>
                          <input
                            id={emailType.code}
                            name={emailType.code}
                            type="checkbox"
                            checked={selectedItems[emailType.code] || false}
                            className="ml-1"
                            onChange={() => clickPreference(emailType)}
                          />
                          <span className="theme-slider round" />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
      }

      {!showAll &&
        <h4 className="mb-3">
          <FormattedMessage
            id="account-preference-center.noticks"
            defaultMessage="Please click the refresh button to update the preference center"
          />
        </h4>
      }
      <div className="btn-account-group">
        {showAll &&
          <div className="btn-account-container btn-account-container-buttons">
            <div>
              <Button
                color="primary"
                className="btn-account"
                onClick={savePreferences}
              >
                <FormattedMessage id="account-preference-center.update-preferences" defaultMessage="Update Preferences" />
              </Button>
            </div>
          </div>
        }
      </div>
    </div >
  );
};

PreferenceCenter.propTypes = {
  navigationClick: PropTypes.func,

};

PreferenceCenter.defaultProps = {
  navigationClick: () => {},
};

export default PreferenceCenter;
